import type { CSSProperties, ReactElement } from 'react';
import { useNavigate } from 'react-router';

import { LanguageRoute } from '@/config/router/routes';

import { whiteAlpha } from '@/config/appColors';
import type { TextController } from '@/presentation/widgets/PrimaryInputField';
import { SvgAsset } from '@/presentation/widgets/SvgAsset';
import { lightenColor } from '@/utils/extensions/colorExtension';
import { wv } from '@/utils/screenUtil';

import {
  isBackspace,
  isClose,
  isEmpty,
  type DigitKeyboardItem,
} from './digitKeyboardItem';
import styles from './DigitButton.module.css';

/**
 * Перенос
 * `lib/src/presentation/pages/verify_admin/widgets/digit_keyboard/digit_button.dart`.
 *
 * Дерево в Dart:
 *   ClipOval → Material(transparency) → InkWell(customBorder: CircleBorder,
 *   highlightColor, splash прозрачный) → Ink(круг, цвет, тень) → Center.
 */

/** `Colors.transparent` */
const TRANSPARENT = 'transparent';

/** `Colors.white.withValues(alpha: 0.2)` */
const BACKGROUND_COLOR = whiteAlpha(0.2);

/** `Colors.white.withValues(alpha: 0.05)` */
const SHADOW_COLOR = whiteAlpha(0.05);

/** `BoxShadow(offset: Offset(0, 0), spreadRadius: 10, blurRadius: 10)` */
const BOX_SHADOW = `0 0 10px 10px ${SHADOW_COLOR}`;

/** `backgroundColor.lightenColor(0.9)` — highlightColor у InkWell. */
const OVERLAY_COLOR = lightenColor(BACKGROUND_COLOR, 0.9);

/** `SvgAsset(height: 30.w)` у «закрыть» и «стереть». */
const ICON_HEIGHT = 30;

/** `Colors.white` — перекраска только у иконки «закрыть». */
const WHITE = '#ffffff';

export interface DigitButtonProps {
  readonly item: DigitKeyboardItem;
  /** Готовая CSS-длина стороны круга, например `wv(54)`. */
  readonly size: string;
  /** `TextEditingController` — общий с полем пин-кода. */
  readonly controller: TextController;
}

export function DigitButton({ item, size, controller }: DigitButtonProps): ReactElement {
  const navigate = useNavigate();
  const isTransparent = isBackspace(item) || isEmpty(item) || isClose(item);
  const backgroundColor = isTransparent ? TRANSPARENT : BACKGROUND_COLOR;
  const overlayColor = isTransparent ? TRANSPARENT : OVERLAY_COLOR;

  const handleClick = (): void => {
    switch (item.kind) {
      case 'close':
        // `context.pop()` — родителем экрана PIN всегда был выбор языка.
        LanguageRoute.go(navigate);
        return;
      case 'key':
        controller.setValue(`${controller.value}${item.digit}`);
        return;
      case 'empty':
        return;
      case 'backspace': {
        const text = controller.value;
        if (text.length > 0) {
          controller.setValue(text.substring(0, text.length - 1));
        }
        return;
      }
    }
  };

  const style = {
    '--digit-size': size,
    '--background-color': backgroundColor,
    '--overlay-color': overlayColor,
    '--box-shadow': isTransparent ? 'none' : BOX_SHADOW,
  } as CSSProperties;

  return (
    <div className={styles.clip} style={style}>
      <button type="button" className={styles.button} onClick={handleClick}>
        <DigitButtonContent item={item} />
      </button>
    </div>
  );
}

interface DigitButtonContentProps {
  readonly item: DigitKeyboardItem;
}

/** `switch (item)` внутри `Center`: иконка, цифра или пустота. */
function DigitButtonContent({ item }: DigitButtonContentProps): ReactElement | null {
  switch (item.kind) {
    case 'close':
      return (
        <SvgAsset
          icon="cancel"
          color={WHITE}
          height={wv(ICON_HEIGHT)}
          className={styles.icon}
        />
      );
    case 'backspace':
      return (
        <SvgAsset icon="backspace" height={wv(ICON_HEIGHT)} className={styles.icon} />
      );
    case 'key':
      return <span className={styles.digit}>{item.digit}</span>;
    case 'empty':
      // SizedBox.shrink()
      return null;
  }
}
